import Link from '@/components/Link'

import { SocialMediaType } from "@/types";

type SocialMediaProps = {
    value: SocialMediaType[]
    theme: 'dark' | 'light'
    external?: boolean
}

function SocialMedia ({ value, theme = 'dark', external }: SocialMediaProps) {
    return (
        <div className="flex justify-center md:justify-start gap-6">
            {value.map((socialMedia, key) => (
                <SocialMediaIcon
                    icon={socialMedia.medium.toLowerCase() as SocialMediaIconProps['icon']}
                    link={socialMedia.link}
                    theme={theme}
                    key={key} />
            ))}
        </div>
    )
}

type SocialMediaIconProps = {
    icon: keyof typeof icons
    link: SocialMediaType['link']
    theme: 'dark' | 'light'
}

function SocialMediaIcon ({ icon, link, theme }: SocialMediaIconProps) {
    const svgProps: any = {
        className: 'w-6 h-6 ' + themes[theme],
        'aria-hidden': "true",
        xmlns: 'http://www.w3.org/2000/svg',
        width: 24,
        height: 24,
        fill: 'currentColor',
        viewBox: '0 0 24 24',
    }
    const Path = icons[icon]
    return (
        <Link href={link} target="_blank" absolute>
            <svg {...svgProps}>
                <Path />
            </svg>
        </Link>
    )
}

const icons = {
    facebook: FacebookIcon,
    instagram: InstagramIcon,
    website: WebsiteIcon,
    twitter: TwitterIcon,
    linkedin: LinkedinIcon,
    pinterest: PinterestIcon,
    youtube: YoutubeIcon
}

const themes = {
    dark: 'text-black',
    light: 'text-white'
}

function FacebookIcon() {
    return (
        <path
            fillRule="evenodd"
            d="M13.135 6H15V3h-1.865a4.147 4.147 0 0 0-4.142 4.142V9H7v3h2v9.938h3V12h2.021l.592-3H12V6.591A.6.6 0 0 1 12.592 6h.543Z"
            clipRule="evenodd"
        />
    )
}

function InstagramIcon() {
    return (
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M3 8a5 5 0 0 1 5-5h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8Zm5-3a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H8Zm7.597 2.214a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2h-.01a1 1 0 0 1-1-1ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm-5 3a5 5 0 1 1 10 0 5 5 0 0 1-10 0Z"
            clipRule="evenodd"
        />

    )
}

function WebsiteIcon () {
    return (
        <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 15v5m-3 0h6M4 11h16M5 15h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1Z"/>

    )
}

function TwitterIcon () {
    return (
        <path
            fillRule="evenodd"
            d="M22 5.892a8.178 8.178 0 0 1-2.355.635 4.074 4.074 0 0 0 1.8-2.235 8.343 8.343 0 0 1-2.605.981A4.13 4.13 0 0 0 15.85 4a4.068 4.068 0 0 0-4.1 4.038c0 .31.035.618.105.919A11.705 11.705 0 0 1 3.4 4.734a4.006 4.006 0 0 0 1.268 5.392 4.165 4.165 0 0 1-1.859-.5v.05A4.057 4.057 0 0 0 6.1 13.635a4.192 4.192 0 0 1-1.856.07 4.108 4.108 0 0 0 3.831 2.807A8.36 8.36 0 0 1 2 18.184 11.732 11.732 0 0 0 8.291 20 11.502 11.502 0 0 0 19.964 8.5c0-.177 0-.349-.012-.523A8.143 8.143 0 0 0 22 5.892Z"
            clipRule="evenodd"/>

    )
}

function LinkedinIcon () {
    return (
        <>
            <path
                fillRule="evenodd"
                d="M12.51 8.796v1.697a3.738 3.738 0 0 1 3.288-1.684c3.455 0 4.202 2.16 4.202 4.97V19.5h-3.2v-5.072c0-1.21-.244-2.766-2.128-2.766-1.827 0-2.139 1.317-2.139 2.676V19.5h-3.19V8.796h3.168ZM7.2 6.106a1.61 1.61 0 0 1-.988 1.483 1.595 1.595 0 0 1-1.743-.348A1.607 1.607 0 0 1 5.6 4.5a1.601 1.601 0 0 1 1.6 1.606Z"
                clipRule="evenodd"/>
            <path d="M7.2 8.809H4V19.5h3.2V8.809Z"/>
        </>
    )
}

function PinterestIcon () {
    return (
        <path
            d="M7.55 23.12c-.15-1.36-.04-2.67.25-3.93L9 14.02a7 7 0 0 1-.34-2.07c0-1.68.8-2.88 2.08-2.88.88 0 1.53.62 1.53 1.8q0 .57-.22 1.28l-.53 1.73q-.15.5-.15.91c0 1.2.92 1.88 2.09 1.88 2.08 0 3.57-2.16 3.57-4.96 0-3.12-2.04-5.11-5.06-5.11-3.36 0-5.49 2.19-5.49 5.23 0 1.23.38 2.37 1.11 3.15-.24.4-.5.48-.88.48-1.2 0-2.34-1.7-2.34-4 0-3.99 3.2-7.16 7.68-7.16 4.7 0 7.66 3.28 7.66 7.33 0 4.07-2.88 7.13-5.98 7.13a3.8 3.8 0 0 1-3.07-1.47l-.61 2.5c-.33 1.28-.83 2.5-1.62 3.67A12 12 0 0 0 24 11.99 12 12 0 1 0 7.55 23.12"></path>
    )
}

function YoutubeIcon() {
    return (
        <path
            fillRule="evenodd"
            d="M21.7 8.037a4.26 4.26 0 0 0-.789-1.964 2.84 2.84 0 0 0-1.984-.839c-2.767-.2-6.926-.2-6.926-.2s-4.157 0-6.928.2a2.836 2.836 0 0 0-1.983.839 4.225 4.225 0 0 0-.79 1.965 30.146 30.146 0 0 0-.2 3.206v1.5a30.12 30.12 0 0 0 .2 3.206c.094.712.364 1.39.784 1.972.604.536 1.38.837 2.187.848 1.583.151 6.731.2 6.731.2s4.161 0 6.928-.2a2.844 2.844 0 0 0 1.985-.84 4.27 4.27 0 0 0 .787-1.965 30.12 30.12 0 0 0 .2-3.206v-1.516a30.672 30.672 0 0 0-.202-3.206Zm-11.692 6.554v-5.62l5.4 2.819-5.4 2.801Z"
            clipRule="evenodd"
        />
    )
}

export default SocialMedia;

export {SocialMediaIcon}